import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaUsers,
  FaVideo,
  FaChartBar,
  FaCog,
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaTimes,
  FaClock,
  FaPlay,
  FaEdit,
  FaTrash,
  FaFilter,
  FaSearch,
  FaPlus,
  FaCalendarCheck,
  FaMoneyCheckAlt,
  FaBook,
  FaTasks,
  FaImages
} from 'react-icons/fa';
import GalleryManagement from '../components/Admin/GalleryManagement';
import VideoUpload from '../components/Admin/VideoUpload';
import PaymentVerifications from '../components/Admin/PaymentVerifications';
import LessonManagement from '../components/Admin/LessonManagement';
import SubmissionsDashboard from '../components/Admin/SubmissionsDashboard';
import HomeworkManager from '../components/Admin/HomeworkManager';
import HomeworkSubmissions from '../components/Admin/HomeworkSubmissions';
import api from '../services/api';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoFilter, setVideoFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'student' });
  const [editingVideo, setEditingVideo] = useState(null);
  const [videoEditForm, setVideoEditForm] = useState({
    title: '',
    description: '',
    category: 'beginner',
    homework: '',
    lesson: '',
    order: 0
  });
  const [lessonsList, setLessonsList] = useState([]);
  // Stats for analytics
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchVideos();
    fetchStats();
    fetchLessonsList();
  }, []);

  const fetchLessonsList = async () => {
    try {
      const res = await api.get('/lessons');
      if (res.data.success) setLessonsList(res.data.data);
    } catch (e) { console.error(e); }
  };

  const handleOpenEditVideo = (video) => {
    setEditingVideo(video);
    setVideoEditForm({
      title: video.title || '',
      description: video.description || '',
      category: video.category || 'beginner',
      homework: video.homework || '',
      lesson: video.lesson?._id || video.lesson || '',
      order: video.order !== undefined ? video.order : 0
    });
  };

  const saveEditVideo = async () => {
    if (!editingVideo) return;
    try {
      const res = await api.put(`/admin/videos/${editingVideo._id}`, videoEditForm);
      if (res.data.success) {
        setEditingVideo(null);
        fetchVideos();
      } else {
        alert(res.data.message || 'Failed to update video');
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Error updating video');
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/dashboard');
      if (res.data.success) setStats(res.data.data.stats);
    } catch (e) { console.error(e); }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.data.users || response.data.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await api.get('/admin/videos');
      setVideos(response.data.data.videos || response.data.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const toggleUserVideoAccess = async (userId, currentAccess) => {
    try {
      const response = await api.patch(`/admin/users/${userId}/video-access`, {
        canViewVideos: !currentAccess
      });

      setUsers(users.map(user =>
        user._id === userId
          ? { ...user, canViewVideos: !currentAccess }
          : user
      ));

      // Show success message
      alert(`Video access ${!currentAccess ? 'granted' : 'revoked'} successfully`);
    } catch (error) {
      console.error('Error updating video access:', error);
      alert('Error updating video access');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(users.filter(u => u._id !== userId));
      alert('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.message || 'Error deleting user');
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditForm({ name: user.name, role: user.role });
  };

  const saveEditUser = async () => {
    try {
      await api.put(`/admin/users/${editingUser._id}`, editForm);
      setUsers(users.map(u => u._id === editingUser._id ? { ...u, ...editForm } : u));
      setEditingUser(null);
      alert('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      alert(error.response?.data?.message || 'Error updating user');
    }
  };

  const updateVideoStatus = async (videoId, status, rejectionReason = '') => {
    try {
      const response = await api.patch(`/videos/${videoId}/status`, {
        status,
        rejectionReason
      });

      setVideos(videos.map(video =>
        video._id === videoId
          ? { ...video, status, rejectionReason }
          : video
      ));

      alert(`Video ${status} successfully`);
    } catch (error) {
      console.error('Error updating video status:', error);
      alert('Error updating video status');
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video? This cannot be undone.')) return;
    try {
      await api.delete(`/admin/videos/${videoId}`);
      setVideos(videos.filter(v => v._id !== videoId));
      alert('Video deleted successfully');
    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Error deleting video');
    }
  };

  const filteredVideos = videos.filter(video => {
    const matchesFilter = videoFilter === 'all' || video.status === videoFilter;
    const matchesSearch = video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      video.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 transition-colors duration-500">
      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Edit User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={saveEditUser}
                className="flex-1 bg-orange-600 text-white py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={() => setEditingUser(null)}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Video Modal */}
      {editingVideo && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700 mb-5">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <FaEdit className="text-orange-600" /> Edit Video Details
              </h3>
              <button
                onClick={() => setEditingVideo(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={videoEditForm.title}
                  onChange={(e) => setVideoEditForm({ ...videoEditForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={videoEditForm.description}
                  onChange={(e) => setVideoEditForm({ ...videoEditForm, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                  placeholder="Enter video description..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                  <select
                    value={videoEditForm.category}
                    onChange={(e) => setVideoEditForm({ ...videoEditForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                    <option value="theory">Theory</option>
                    <option value="practice">Practice</option>
                    <option value="performance">Performance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Order</label>
                  <input
                    type="number"
                    value={videoEditForm.order}
                    onChange={(e) => setVideoEditForm({ ...videoEditForm, order: parseInt(e.target.value, 10) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Assign to Lesson</label>
                <select
                  value={videoEditForm.lesson}
                  onChange={(e) => setVideoEditForm({ ...videoEditForm, lesson: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                >
                  <option value="">No Lesson (Standalone)</option>
                  {lessonsList.map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.title} (Lesson {l.lessonNumber || ''})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Homework Instruction (Optional)
                </label>
                <textarea
                  value={videoEditForm.homework}
                  onChange={(e) => setVideoEditForm({ ...videoEditForm, homework: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                  placeholder="e.g. Practice Teental bols at 60 bpm"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={saveEditVideo}
                className="flex-1 bg-orange-600 text-white py-2.5 rounded-lg font-medium hover:bg-orange-700 transition shadow-sm"
              >
                Save Changes
              </button>
              <button
                onClick={() => setEditingVideo(null)}
                className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2.5 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Manage users, videos, and platform settings</p>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'users', label: 'Users', icon: FaUsers },
                { id: 'videos', label: 'Videos', icon: FaVideo },
                { id: 'payments', label: 'Approvals', icon: FaMoneyCheckAlt },
                { id: 'upload', label: 'Add Video', icon: FaPlus },
                { id: 'lessons', label: 'Lessons', icon: FaCalendarCheck },
                { id: 'submissions', label: 'Submissions', icon: FaCheck },
                { id: 'homework', label: 'Homework', icon: FaBook },
                { id: 'hw-submissions', label: 'HW Sub', icon: FaTasks },
                { id: 'analytics', label: 'Analytics', icon: FaChartBar },
                { id: 'gallery', label: 'Gallery', icon: FaImages },
                { id: 'settings', label: 'Settings', icon: FaCog }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                    }`}
                >
                  <tab.icon className="mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm"
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">User Management</h2>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Manage user accounts and video access permissions</p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Video Access
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center">
                            <span className="text-white font-medium">
                              {user.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-blue-100 text-blue-800'
                          }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.isPremium ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Premium
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            Free
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {user.canViewVideos ? (
                            <div className="flex items-center text-green-600">
                              <FaEye className="mr-2" />
                              <span className="text-sm font-medium">Granted</span>
                            </div>
                          ) : (
                            <div className="flex items-center text-red-600">
                              <FaEyeSlash className="mr-2" />
                              <span className="text-sm font-medium">Restricted</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => toggleUserVideoAccess(user._id, user.canViewVideos)}
                            className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium transition-colors ${user.canViewVideos
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                          >
                            {user.canViewVideos ? (
                              <>
                                <FaEyeSlash className="mr-1" />
                                Revoke Access
                              </>
                            ) : (
                              <>
                                <FaEye className="mr-1" />
                                Grant Access
                              </>
                            )}
                          </button>

                          <button
                            onClick={() => openEditUser(user)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit user"
                          >
                            <FaEdit />
                          </button>

                          {user.role !== 'admin' && (
                            <button
                              onClick={() => deleteUser(user._id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete user"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm"
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Video Management</h2>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">Review and approve user-submitted videos</p>
                </div>

                <div className="mt-4 sm:mt-0 flex items-center space-x-4">
                  {/* Search */}
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search videos..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Filter */}
                  <select
                    value={videoFilter}
                    onChange={(e) => setVideoFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="all">All Videos</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 p-6">
              {filteredVideos.map((video) => (
                <div key={video._id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white dark:bg-gray-800">
                  <div className="relative">
                    <div className="aspect-w-16 aspect-h-9 bg-gray-100 flex items-center justify-center">
                      {(video.videoUrl && (video.videoUrl.includes('youtube.com') || video.videoUrl.includes('youtu.be'))) || (video.driveViewLink && video.driveViewLink.includes('youtube.com')) ? (
                        <img
                          src={(() => {
                            const url = video.videoUrl || video.driveViewLink || '';
                            const ytMatch = url.match(/embed\/([a-zA-Z0-9_-]{11})/);
                            const ytId = ytMatch ? ytMatch[1] : (video.driveFileId ? video.driveFileId.replace('youtube_', '') : '');
                            return ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : (video.thumbnail || '/images/default-video.jpg');
                          })()}
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          className="w-full h-full object-cover"
                          poster={video.thumbnail}
                          preload="metadata"
                        >
                          <source src={video.videoUrl} type="video/mp4" />
                        </video>
                      )}
                    </div>

                    <div className="absolute top-2 right-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(video.status)}`}>
                        {video.status}
                      </span>
                    </div>

                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <FaPlay className="text-white text-2xl" />
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">{video.title}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">{video.description}</p>

                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <span>By: {video.uploadedBy?.name}</span>
                      <span>{new Date(video.createdAt).toLocaleDateString()}</span>
                    </div>

                    {video.status === 'pending' ? (
                      <div className="flex space-x-2 mb-2">
                        <button
                          onClick={() => updateVideoStatus(video._id, 'approved')}
                          className="flex-1 bg-green-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors flex items-center justify-center"
                        >
                          <FaCheck className="mr-1" />
                          Publish
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Rejection reason (optional):');
                            if (reason !== null) {
                              updateVideoStatus(video._id, 'rejected', reason);
                            }
                          }}
                          className="flex-1 bg-yellow-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-yellow-700 transition-colors flex items-center justify-center"
                        >
                          <FaTimes className="mr-1" />
                          Reject
                        </button>
                      </div>
                    ) : video.status === 'approved' ? (
                      <div className="flex space-x-2 mb-2">
                        <button
                          onClick={() => updateVideoStatus(video._id, 'pending')}
                          className="flex-1 bg-gray-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-700 transition-colors flex items-center justify-center"
                        >
                          <FaEyeSlash className="mr-1" />
                          Unpublish
                        </button>
                      </div>
                    ) : null}

                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={() => deleteVideo(video._id)}
                        className="flex-1 bg-red-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-red-700 transition-colors flex items-center justify-center"
                      >
                        <FaTrash className="mr-1" />
                        Delete
                      </button>
                    </div>

                    {video.status === 'rejected' && video.rejectionReason && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                        <strong>Rejection reason:</strong> {video.rejectionReason}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {filteredVideos.length === 0 && (
              <div className="text-center py-12">
                <FaVideo className="mx-auto text-4xl text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No videos found</h3>
                <p className="text-gray-500">No videos match your current filter criteria.</p>
              </div>
            )}
          </motion.div>
        )}



        {activeTab === 'upload' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <VideoUpload onUploadSuccess={() => { setActiveTab('videos'); fetchVideos(); }} />
          </motion.div>
        )}

        {/* Payment Approvals Tab */}
        {activeTab === 'payments' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <PaymentVerifications />
          </motion.div>
        )}

        {/* Lessons Tab */}
        {activeTab === 'lessons' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <LessonManagement />
          </motion.div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <SubmissionsDashboard />
          </motion.div>
        )}

        {/* Homework Tab */}
        {activeTab === 'homework' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <HomeworkManager />
          </motion.div>
        )}

        {/* HW Submissions Tab */}
        {activeTab === 'hw-submissions' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <HomeworkSubmissions />
          </motion.div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { label: 'Total Users', value: stats?.totalUsers ?? '...', color: 'bg-blue-50 text-blue-700', border: 'border-blue-200' },
                { label: 'Total Students', value: stats?.totalStudents ?? '...', color: 'bg-green-50 text-green-700', border: 'border-green-200' },
                { label: 'Total Videos', value: stats?.totalVideos ?? '...', color: 'bg-orange-50 text-orange-700', border: 'border-orange-200' },
                { label: 'Active Videos', value: stats?.activeVideos ?? '...', color: 'bg-teal-50 text-teal-700', border: 'border-teal-200' },
                { label: 'Total Teachers', value: stats?.totalTeachers ?? '...', color: 'bg-purple-50 text-purple-700', border: 'border-purple-200' },
                { label: 'Total Classes', value: stats?.totalClasses ?? '...', color: 'bg-red-50 text-red-700', border: 'border-red-200' },
              ].map(card => (
                <div key={card.label} className={`rounded-xl p-6 border-2 ${card.color} ${card.border}`}>
                  <p className="text-sm font-medium opacity-70">{card.label}</p>
                  <p className="text-4xl font-bold mt-2">{card.value}</p>
                </div>
              ))}
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">User Breakdown</h3>
              {stats && (
                <div className="space-y-3">
                  {[
                    { label: 'Students', value: stats.totalStudents, total: stats.totalUsers, color: 'bg-green-500' },
                    { label: 'Teachers', value: stats.totalTeachers, total: stats.totalUsers, color: 'bg-purple-500' },
                  ].map(item => (
                    <div key={item.label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700">{item.label}</span>
                        <span className="text-gray-500">{item.value} / {item.total}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`${item.color} h-3 rounded-full transition-all duration-700`}
                          style={{ width: item.total ? `${Math.round((item.value / item.total) * 100)}%` : '0%' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <GalleryManagement />
          </motion.div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Platform Settings</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-5 bg-orange-50 border border-orange-200 rounded-xl">
                <h3 className="font-semibold text-orange-800 mb-1">Video Access</h3>
                <p className="text-sm text-orange-700 mb-3">Control which students can view videos. Go to the <strong>Users</strong> tab to grant or revoke access per student.</p>
                <button onClick={() => setActiveTab('users')} className="px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition">
                  Manage Users
                </button>
              </div>
              <div className="p-5 bg-blue-50 border border-blue-200 rounded-xl">
                <h3 className="font-semibold text-blue-800 mb-1">Lesson Management</h3>
                <p className="text-sm text-blue-700 mb-3">Create and organize lessons. Assign videos to lessons to enable the step-by-step learning flow for students.</p>
                <button onClick={() => setActiveTab('lessons')} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition">
                  Manage Lessons
                </button>
              </div>
              <div className="p-5 bg-green-50 border border-green-200 rounded-xl">
                <h3 className="font-semibold text-green-800 mb-1">Submission Grading</h3>
                <p className="text-sm text-green-700 mb-3">Review student practice videos and assign a grade out of 10. Approval unlocks the next video for the student.</p>
                <button onClick={() => setActiveTab('submissions')} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition">
                  View Submissions
                </button>
              </div>
              <div className="p-5 bg-purple-50 border border-purple-200 rounded-xl">
                <h3 className="font-semibold text-purple-800 mb-1">Payment Approvals</h3>
                <p className="text-sm text-purple-700 mb-3">Review and approve manual payment receipts uploaded by students to grant premium access.</p>
                <button onClick={() => setActiveTab('payments')} className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition">
                  View Payments
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;